'use client';
import React, { useEffect } from 'react';
import './style.css';

interface AppSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  showOverlay?: boolean;
  position?: 'left' | 'right';
  className?: string;
  overlayClassName?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  title?: string;
  showCloseButton?: boolean;
  footer?: React.ReactNode;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  isOpen,
  onClose,
  children,
  showOverlay = true,
  position = 'left',
  className = '',
  overlayClassName = '',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  title,
  showCloseButton = true,
  footer,
}) => {
  // Fechar com ESC
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeOnEscape, isOpen, onClose]);

  useEffect(() => {
    if (isOpen && showOverlay) {
      const currentOverflow = document.body.style.overflow;
      if (currentOverflow !== 'hidden') {
        document.body.style.overflow = 'hidden';
      }
    }

    if (!isOpen) {
      document.body.style.overflow = 'visible';
    }

    return () => {
      if (!isOpen) {
        // Só restaura o overflow se não há outros overlays ativos
        const activeOverlays = document.querySelectorAll('.dialog-overlay, .app-sidebar-overlay');
        if (activeOverlays.length <= 1) {
          document.body.style.overflow = '';
        }
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      onClose();
    }
  };

  return (
    <>
      {showOverlay && <div className={`app-sidebar-overlay ${overlayClassName}`} onClick={handleOverlayClick} />}

      <div className={`app-sidebar app-sidebar--${position} ${className}`} onClick={(e) => e.stopPropagation()}>
        {(title || showCloseButton) && (
          <div className="app-sidebar__header">
            {title && <h3 className="app-sidebar__title">{title}</h3>}
            {showCloseButton && (
              <button className="app-sidebar__close-button" onClick={onClose} type="button" aria-label="Fechar sidebar">
                <span>X</span>
              </button>
            )}
          </div>
        )}

        <div className="app-sidebar__content">{children}</div>

        {footer && <div className="app-sidebar__footer">{footer}</div>}
      </div>
    </>
  );
};

export default AppSidebar;
export type { AppSidebarProps };
